import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  ChevronRight, Loader2, MapPin, ExternalLink, Phone,
  Globe, Briefcase, Search, Star, MessageCircle, Plus, Check, Send, BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApisAIFooter from '@/components/ApisAIFooter';
import CatalogConcierge from '@/components/CatalogConcierge';
import { BreadcrumbSchema } from '@/components/BreadcrumbSchema';
import { API } from '@/utils/api';

export default function BusinessCatalogPage() {
  const { category, city } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = city
      ? `${API}/public/katalog/${category}/${city}`
      : `${API}/public/katalog/${category}`;
    axios.get(url).then(res => setData(res.data)).catch(console.error).finally(() => setLoading(false));
  }, [category, city]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  const { category: cat, businesses = [], recommendations = [], nearby = [], count = 0, cities = [], seo = {}, faq = [], page_description = '', related_blog = [] } = data || {};
  const catName = cat?.plural || category;
  const cityName = data?.city || '';

  const allListings = [
    ...businesses.map(b => ({ type: 'business', ...b })),
    ...recommendations.map(r => ({
      type: 'recommendation',
      name: r.business_name,
      city: r.business_city,
      description: r.business_description,
      phone: r.business_phone,
      email: r.business_email,
      website: r.business_website,
      facebook: r.business_facebook,
      instagram: r.business_instagram,
      recommendation_text: r.recommendation_text,
      recommender_name: r.recommender_name,
    }))
  ];

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={`https://apisai.pl${seo.canonical}`} />
        {seo.schema && <script type="application/ld+json">{JSON.stringify(seo.schema)}</script>}
        {seo.faq_schema && <script type="application/ld+json">{JSON.stringify(seo.faq_schema)}</script>}
        {seo.og && (
          <>
            <meta property="og:title" content={seo.og.title} />
            <meta property="og:description" content={seo.og.description} />
            <meta property="og:type" content={seo.og.type} />
            <meta property="og:url" content={seo.og.url} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seo.og.title} />
            <meta name="twitter:description" content={seo.og.description} />
          </>
        )}
      </Helmet>
      <BreadcrumbSchema breadcrumbs={seo.breadcrumbs} />

      <div className="min-h-screen bg-[#0B0F1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0F1A]/80 backdrop-blur-xl border-b border-white/5">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.svg" alt="ApisAI" className="w-9 h-9" />
              <span className="font-semibold text-lg text-white">ApisAI</span>
            </Link>
            <Link to="/rejestracja">
              <Button className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 rounded-full px-6 text-sm">
                Dodaj swoją firmę
              </Button>
            </Link>
          </div>
        </nav>

        {/* Breadcrumbs */}
        <div className="pt-24 pb-4">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex items-center gap-2 text-sm text-gray-400 flex-wrap" data-testid="breadcrumbs">
              {seo.breadcrumbs?.map((b, i) => (
                <span key={i} className="flex items-center gap-2">
                  {i > 0 && <ChevronRight className="w-4 h-4" />}
                  {i === seo.breadcrumbs.length - 1 ? (
                    <span className="text-white">{b.name}</span>
                  ) : (
                    <Link to={b.url} className="hover:text-white transition-colors">{b.name}</Link>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3" data-testid="page-title">
                {seo.h1 || catName}
              </h1>
              <p className="text-lg text-gray-400">{count} firm w katalogu</p>
            </motion.div>

            {/* AI-generated page description */}
            {page_description && (
              <div className="mb-8 p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06]" data-testid="page-description">
                <p className="text-gray-300 text-sm leading-relaxed">{page_description}</p>
              </div>
            )}

            {/* City filter pills (only on category page without city) */}
            {!city && cities?.length > 0 && (
              <div className="mb-8">
                <div className="flex flex-wrap gap-2 justify-center" data-testid="city-filters">
                  {cities.map(c => (
                    <Link
                      key={c.slug}
                      to={`/katalog/${category}/${c.slug}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-400 text-sm hover:bg-emerald-500/10 hover:border-emerald-500/30 hover:text-emerald-400 transition-all"
                    >
                      <MapPin className="w-3 h-3" />
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* AI Koncierż — tryb „firmy" (zablokowany, bez przełącznika) */}
            <div className="mb-10" data-testid="business-concierge">
              <CatalogConcierge lockMode="firmy" />
            </div>

            {/* Listings */}
            {allListings.length > 0 ? (
              <div className="grid gap-4" data-testid="listings">
                {allListings.map((item, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                    <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-6 hover:border-emerald-500/20 transition-colors">
                      <div className="flex items-start gap-5">
                        <div className="w-14 h-14 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                          <Briefcase className="w-7 h-7 text-emerald-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className="text-lg font-semibold text-white">{item.name}</h3>
                              {item.city && (
                                <div className="flex items-center gap-1.5 text-gray-400 text-sm mt-1">
                                  <MapPin className="w-3.5 h-3.5" />
                                  {item.city}
                                </div>
                              )}
                            </div>
                            {item.type === 'recommendation' && (
                              <span className="flex-shrink-0 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium">
                                Polecana
                              </span>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-gray-400 text-sm mt-2 line-clamp-2">{item.description}</p>
                          )}
                          {item.recommendation_text && (
                            <div className="mt-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                              <div className="flex items-center gap-1.5 text-amber-400 text-xs mb-1">
                                <Star className="w-3 h-3" />
                                Polecenie od {item.recommender_name || 'użytkownika'}
                              </div>
                              <p className="text-gray-300 text-sm">{item.recommendation_text}</p>
                            </div>
                          )}
                          <div className="flex items-center gap-3 mt-3 flex-wrap">
                            {item.phone && (
                              <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300" data-testid={`phone-${i}`}>
                                <Phone className="w-3.5 h-3.5" />
                                {item.phone}
                              </a>
                            )}
                            {item.website && (
                              <a href={item.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300">
                                <Globe className="w-3.5 h-3.5" />
                                Strona www
                              </a>
                            )}
                            {item.facebook && (
                              <a href={item.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300">
                                <ExternalLink className="w-3.5 h-3.5" />
                                Facebook
                              </a>
                            )}
                            {item.slug && (
                              <Link to={`/firma/${item.slug}`} className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300">
                                <ExternalLink className="w-3.5 h-3.5" />
                                Profil
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-12 text-center" data-testid="empty-state">
                <Briefcase className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  Brak firm w tej kategorii{cityName ? ` w ${cityName}` : ''}
                </h3>
                <p className="text-gray-400 mb-6">Bądź pierwszy! Dodaj swoją firmę do katalogu za darmo.</p>
                <Link to="/rejestracja">
                  <Button data-testid="empty-register" className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-8">
                    Dodaj swoją firmę
                  </Button>
                </Link>
              </div>
            )}

            {/* Nearby */}
            {nearby?.length > 0 && (
              <div className="mt-10">
                <h2 className="text-xl font-bold text-white mb-4">Firmy w pobliżu</h2>
                <div className="grid gap-3">
                  {nearby.map((b, i) => (
                    <div key={i} className="rounded-xl bg-white/[0.02] border border-white/5 p-4 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm">{b.name}</p>
                        <p className="text-gray-500 text-xs">{b.city}</p>
                      </div>
                      {b.slug && (
                        <Link to={`/firma/${b.slug}`} className="text-emerald-400 text-sm hover:underline">
                          Zobacz
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Related Blog */}
            {related_blog.length > 0 && (
              <div className="mt-12" data-testid="related-blog-section">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-amber-400" />
                  Z bloga — porady ekspertów
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {related_blog.map((a, i) => (
                    <Link
                      key={i}
                      to={`/blog/${a.slug}`}
                      className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-amber-500/40 transition-colors group"
                      data-testid={`related-blog-${i}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-5 h-5 text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm group-hover:text-amber-400 transition-colors line-clamp-2">{a.title}</p>
                        {a.city && <p className="text-gray-500 text-xs mt-0.5">{a.city}</p>}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-amber-400 flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* FAQ */}
            {faq?.length > 0 && (
              <div className="mt-12" data-testid="faq-section">
                <h2 className="text-xl font-bold text-white mb-6">Najczęściej zadawane pytania</h2>
                <div className="space-y-3">
                  {faq.map((item, i) => (
                    <details key={i} className="group rounded-xl bg-white/[0.03] border border-white/[0.06] overflow-hidden">
                      <summary className="flex items-center justify-between p-5 cursor-pointer text-white font-medium text-sm hover:bg-white/[0.02] transition-colors">
                        {item.q}
                        <ChevronRight className="w-4 h-4 text-gray-500 group-open:rotate-90 transition-transform flex-shrink-0 ml-4" />
                      </summary>
                      <div className="px-5 pb-5 text-gray-400 text-sm leading-relaxed border-t border-white/5 pt-4">
                        {item.a}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            )}

            {/* Internal links */}
            <div className="mt-12">
              <h2 className="text-lg font-bold text-white mb-4">Powiązane</h2>
              <div className="flex flex-wrap gap-2">
                <Link to="/katalog" className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors text-sm">
                  Wszystkie kategorie
                </Link>
                {city && (
                  <Link to={`/katalog/${category}`} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors text-sm">
                    {catName} — cała Polska
                  </Link>
                )}
                <Link to="/rasy" className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors text-sm">
                  Katalog ras psów
                </Link>
                <Link to="/wizytowki" className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors text-sm">
                  Wizytówki online
                </Link>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 text-center p-8 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/20">
              <h2 className="text-xl font-bold text-white mb-2">
                {cat?.name ? `Jesteś ${cat.name.toLowerCase()}em?` : 'Prowadzisz firmę?'}
              </h2>
              <p className="text-gray-400 mb-5">Dodaj się do katalogu i docieraj do nowych klientów lokalnie.</p>
              <Link to="/rejestracja">
                <Button data-testid="cta-register-bottom" className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-8">
                  Dodaj firmę za darmo
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <ApisAIFooter variant="light" pageType="catalog" pageSlug={city ? `${category}-${city}` : category} />
      </div>
    </>
  );
}
